import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { formatConstant } from '@/modules/common/lib/string';
import { PaymentStatus } from '@/types/payment';
import { IPaymentGroup } from '@/types/payment-groups';
import { ArrowForward, Refresh } from '@mui/icons-material';
import { Button, IconButton, Typography } from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import { MRT_ColumnDef } from 'material-react-table';
import { enqueueSnackbar } from 'notistack';
import pluralize from 'pluralize';
import { PaymentStatusChip } from '../PaymentStatusChip';

export const createPaymentGroupsTableColumns = (
  onRetryPayment: (paymentGroup: IPaymentGroup) => void,
  hideActions?: boolean,
): MRT_ColumnDef<IPaymentGroup>[] => {
  const columns: MRT_ColumnDef<IPaymentGroup>[] = [
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      maxSize: 156,
      muiTableHeadCellProps: {
        classes: {
          root: 'ml-10 -mr-10 ',
        },
      },
      Cell: ({
        row: { original: paymentGroup, toggleExpanded, getIsExpanded },
      }) => {
        return (
          <div
            className="flex items-center gap-2"
            onClick={() => toggleExpanded()}
          >
            <IconButton
              size="small"
              color="inherit"
              disabled={!paymentGroup.payments.length}
            >
              <ArrowDropDownIcon
                className={`${getIsExpanded() ? 'rotate-180' : ''}`}
              />
            </IconButton>
            <Typography variant="subtitle2">
              {formatCurrencyValue(paymentGroup.amount, paymentGroup.currency)}
            </Typography>
            <div className="flex flex-col gap-2">
              <PaymentStatusChip status={paymentGroup.status} />
            </div>
          </div>
        );
      },
    },
    {
      id: 'paymentMethod',
      header: 'Payment Method',
      accessorKey: 'method',
      Cell: ({ row: { original } }) => {
        return (
          <div>
            <Typography variant="body2" className="capitalize">
              {formatConstant(original.method)}
            </Typography>
          </div>
        );
      },
    },

    {
      id: 'paymentDate',
      header: 'Payment Date',
      accessorKey: 'createdAt',
      Cell: ({ row: { original } }) => {
        return (
          <div>
            <Typography>
              {isValid(new Date(original.createdAt)) &&
                format(original.createdAt, 'Pp')}
            </Typography>
          </div>
        );
      },
    },
    {
      id: 'numberOfPayments',
      header: 'Nº of Payments',
      accessorKey: 'payments',
      Cell: ({ row: { original } }) => {
        return (
          <div>
            <Typography>
              {pluralize('payment', original.payments.length, true)}
            </Typography>
          </div>
        );
      },
    },

    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      filterVariant: 'checkbox',
      Cell({ row: { original: paymentGroup } }) {
        return (
          <div className="flex flex-col gap-2">
            <PaymentStatusChip status={paymentGroup.status} />
          </div>
        );
      },
    },
  ];

  // Only add actions column if not hidden
  if (!hideActions) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      accessorKey: 'actions',
      grow: true,
      muiTableHeadCellProps: {
        align: 'right',
      },
      muiTableBodyCellProps: {
        align: 'right',
      },
      Cell({ row: { original: paymentGroup } }) {
        return (
          <div className="flex flex-col gap-2">
            {paymentGroup.status === PaymentStatus.Expired ? (
              <Button
                size="small"
                onClick={() => onRetryPayment(paymentGroup)}
                startIcon={<Refresh />}
              >
                Retry payment
              </Button>
            ) : paymentGroup.status === PaymentStatus.InCheckout ? (
              <Button
                size="small"
                startIcon={<ArrowForward />}
                onClick={() => {
                  try {
                    if (!paymentGroup.providerInfo?.checkoutSessionUrl) {
                      throw new Error('No checkout session URL found');
                    }
                    window.open(
                      paymentGroup.providerInfo?.checkoutSessionUrl,
                      '_blank',
                    );
                  } catch {
                    enqueueSnackbar(
                      `Failed to open your payment, please try again later.`,
                      {
                        variant: 'error',
                      },
                    );
                  }
                }}
              >
                Finish your payment
              </Button>
            ) : null}
          </div>
        );
      },
    });
  }

  return columns;
};
